import { useCallback, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { EllipsisVertical } from 'lucide-react'

import { SplitsLink as Link } from '~/components/Link'
import { List } from '~/components/component-library'
import { Dropdown } from '~/components/component-library/Dropdown'
import { StepButtons } from '~/components/create-workspace/StepButtons'
import { RecoverySignerDialog } from '~/components/create-workspace/steps/5_RecoveryStep/RecoverySignerDialog'
import { RecoverySignerDisplay } from '~/components/create-workspace/steps/5_RecoveryStep/RecoverySignerDisplay'
import {
  getRecommendedThreshold,
  ThresholdForm,
} from '~/components/create-workspace/steps/5_RecoveryStep/ThresholdForm'
import { useAuth } from '~/context/AuthProvider'
import {
  ActiveOrgResponse,
  getActiveOrgQueryKey,
  useActiveOrgSmartAccountsSetup,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { IAddress, SmartAccountSetupResponse } from '~/types'

export function RecoveryStep({
  stepForward,
  stepBack,
}: {
  stepForward: () => void
  stepBack: () => void
}) {
  const org = useRequiredActiveOrg()
  const setup = useActiveOrgSmartAccountsSetup()
  const { serverPostRequest } = useServerRequests()
  const { setGlobalModalState } = useAuth()
  const queryClient = useQueryClient()

  const signers = setup?.eoaSigners ?? []
  const signerCount = signers.length

  const defaultRecoveryThreshold =
    setup?.thresholds?.find((t) => t.setupAccount === 'ROOT')?.threshold ??
    getRecommendedThreshold(signerCount)

  const [hasCustomThreshold, setHasCustomThreshold] = useState(false)

  const saveThreshold = useCallback(
    async (threshold: number) => {
      const resp = await serverPostRequest<{
        setup: SmartAccountSetupResponse
      }>({
        path: '/api/org/smart_accounts_setup/set_recovery_threshold',
        params: { orgId: org.id, threshold },
        errorMessage: 'Failed to update recovery threshold',
      })
      if (!resp) return false

      queryClient.setQueryData<ActiveOrgResponse | undefined>(
        getActiveOrgQueryKey(org.id),
        (prev) =>
          prev
            ? {
                ...prev,
                org: { ...prev.org, smartAccountSetup: resp.data.setup },
              }
            : prev,
      )
      return true
    },
    [org.id, queryClient, serverPostRequest],
  )

  const selectThreshold = useCallback(
    async (threshold: number) => {
      const recommendedThreshold = getRecommendedThreshold(signerCount)
      setHasCustomThreshold(threshold !== recommendedThreshold)
      const isSaved = await saveThreshold(threshold)
      if (!isSaved) {
        setHasCustomThreshold(defaultRecoveryThreshold !== recommendedThreshold)
      }
      return isSaved
    },
    [saveThreshold, signerCount, defaultRecoveryThreshold],
  )

  const onSignerCountChanged = useCallback(
    async (newSignerCount: number) => {
      if (hasCustomThreshold || newSignerCount === 0) return
      await saveThreshold(getRecommendedThreshold(newSignerCount))
    },
    [hasCustomThreshold, saveThreshold],
  )

  const closeModal = useCallback(
    () => setGlobalModalState({ isOpen: false, content: null }),
    [setGlobalModalState],
  )

  const openSignerDialog = useCallback(
    (address?: IAddress) => {
      setGlobalModalState({
        isOpen: true,
        content: (
          <RecoverySignerDialog
            address={address}
            onClose={closeModal}
            onSignerAdded={onSignerCountChanged}
          />
        ),
      })
    },
    [setGlobalModalState, closeModal, onSignerCountChanged],
  )

  const removeSigner = useCallback(
    async (keySlot: number) => {
      const resp = await serverPostRequest<{
        setup: SmartAccountSetupResponse
      }>({
        path: '/api/org/smart_accounts_setup/set_eoa_signer_by_keyslot',
        params: { orgId: org.id, address: '', keySlot },
        errorMessage: 'Failed to remove recovery signer',
      })

      if (resp) {
        queryClient.setQueryData<ActiveOrgResponse | undefined>(
          getActiveOrgQueryKey(org.id),
          (prev) =>
            prev
              ? {
                  ...prev,
                  org: { ...prev.org, smartAccountSetup: resp.data.setup },
                }
              : prev,
        )
        await onSignerCountChanged(resp.data.setup?.eoaSigners?.length ?? 0)
      }
    },
    [org.id, queryClient, serverPostRequest, onSignerCountChanged],
  )

  return (
    <>
      <p className="text-primary">
        This defines how your workspace accesses your assets without passkeys.
        Recovery signers act like backup keys and should be secured accordingly.
      </p>
      <List
        label="Recovery signers"
        footer={
          <>
            Updating recovery signers after workspace creation will generate new
            account addresses{' '}
            <Link
              path="https://splits.org/docs/workspaces/recovery#changing-recovery-signers"
              underline
              target="tab"
            >
              Learn more
            </Link>
          </>
        }
        items={signers
          .sort((a, b) => (a.keySlot ?? 0) - (b.keySlot ?? 0))
          .map((signer) => ({
            key: signer.id,
            content: (
              <RecoverySignerDisplay
                address={signer.address}
                name={signer.name}
                email={signer.email}
              />
            ),
            actionIcon: (
              <SignerActions
                onEdit={() => openSignerDialog(signer.address as IAddress)}
                onRemove={() =>
                  signer.keySlot !== undefined && removeSigner(signer.keySlot)
                }
              />
            ),
          }))}
        cta={{
          label: 'Add signer',
          onClick: () => openSignerDialog(),
        }}
      />
      {signerCount > 1 && (
        <ThresholdForm
          defaultRecoveryThreshold={defaultRecoveryThreshold}
          signerCount={signerCount}
          onSelectThreshold={selectThreshold}
        />
      )}
      <StepButtons
        isDisabled={signerCount < 1}
        nextButtonText="Review"
        onNextClick={stepForward}
        onBackClick={stepBack}
      />
    </>
  )
}

const SignerActions = ({
  onEdit,
  onRemove,
}: {
  onEdit: () => void
  onRemove: () => void
}) => (
  <Dropdown
    items={[
      { content: 'Edit', onClick: onEdit },
      { content: 'Remove', onClick: onRemove },
    ]}
  >
    <EllipsisVertical className="h-4 w-4 cursor-pointer text-secondary" />
  </Dropdown>
)
